import { Injectable, Input, signal } from '@angular/core';
import { environment } from '@env/environment';
import { Guid } from '@library-shared/models/guid';
import { tTicket_Session } from '@models/Tables/tTicket_Session';
import notify from 'devextreme/ui/notify';
import moment from 'moment';
import { Log } from '@nhustak/angular-lib';

@Injectable()
export class BaseComponent {
    public dateFormat = "M/d/y";
    public dateTimeFormat = "M/d/y h:mm a";
    public timeFormat = "h:mm a";
    public moment_dateFormat = "M/D/YY";
    public moment_dateTimeFormat = "M/D/YY h:mm a";
    public currencyFormat = "$#,##0.00";
    public date_min = new Date(1920, 1, 1);

    public log: any;
    public readonly env = environment;
    public validationGroup = "validationGroup";
    public componentID: string;
    private autoRefreshInterval: any;
    private _isLoading = false;
    @Input()
    get isLoading(): boolean {
        return this._isLoading;
    }
    set isLoading(value) {
        if (!value && this.loadingMessage !== this.defaultLoadingMessage) {
            this.loadingMessage = this.defaultLoadingMessage;
        }
        this._isLoading = value;
    }

    isLoading2 = signal(false);
    public isActive = false;
    public defaultLoadingMessage = "Working...";
    public loadingMessage = "Working...";
    get viewportWidth(): number {
        return Math.max(
            document.documentElement.clientWidth || 0,
            window.innerWidth || 0
        );
    }
    get viewportHeight(): number {
        return Math.max(
            document.documentElement.clientHeight || 0,
            window.innerHeight || 0
        );
    }

    private spinnerTypes = [
        "ball-scale-multiple",
        "ball-spin",
        "ball-spin-clockwise",
        "ball-spin-clockwise-fade-rotating",
        "ball-spin-fade-rotating",
        "chasing-dots",
        "circle",
        "cube-grid",
        "double-bounce",
        "fading-circle",
        "folding-cube",
        "pulse",
        "rectangle-bounce",
        "rectangle-bounce-party",
        "rectangle-bounce-pulse-out",
        "rectangle-bounce-pulse-out-rapid",
        "rotating-plane",
        "square-jelly-box",
        "square-loader",
        "three-bounce",
        "three-strings",
        "wandering-cubes",
    ];

    public loading_shadingColor = "rgba( 0,0,0,0.4 )";

    get DevOrQA(): boolean {
        return this.env.modeName === "DEV" || this.env.modeName === "QA";
    }

    public linkTarget = "_blank";
    // public linkTarget = '_self';


    constructor() {
        this.componentID =
            "ID" + Guid.newGuid().split("-").join("").substr(0, 15);
        this.log = Log.create(this.constructor.name);

        this.validationGroup = "VG" + this.constructor.name + this.componentID;
        // this.log.data('Validation Group', this.validationGroup);
    }

    public CRtoBR(text: string): string {
        if (!text) {
            return text;
        }
        const find: string[] = ["\r\n", "\n\r", "\r", "\n", "<br/><br/>"];

        find.forEach((findText) => {
            const findAll = new RegExp(findText, "g");
            text = text.replace(findAll, "<br/>");
        });
        return text;
    }

    public customizeColumns(cols) {
        cols.forEach((col) => (col.width = "auto"));
    }

    public selectText(e): void {
        const inputEl = e.element.querySelector(".dx-texteditor-input");
        inputEl.selectionStart = 0;
        inputEl.selectionEnd = inputEl.value.length;
    }

    public getToolipID(tooltipTarget): string {
        return tooltipTarget + this.componentID;
        // return (tooltipTarget + this.componentID).substring(0, 20);
    }

    public getUniqueID(idPrefix: string): string {
        //return idPrefix + this.componentID;
        return (idPrefix + this.componentID).substring(0, 20);
    }
    public getUniqueIDHash(idPrefix): string {
        // return '#' + idPrefix + this.componentID;
        return "#" + (idPrefix + this.componentID).substring(0, 20);
    }

    public isValidEmail(email: string) {
        const re =
            /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
        return re.test(String(email).toLowerCase());
    }

    public startAutoRefresh(func) {
        this.stopAutoRefresh();
        this.autoRefreshInterval = setInterval(func, 5000);
    }

    public stopAutoRefresh() {
        clearInterval(this.autoRefreshInterval);
        this.autoRefreshInterval = null;
    }

    public isAutoRefreshActive(): boolean {
        return this.autoRefreshInterval ? true : false;
    }

    public applyMixins(derivedCtor: any, baseCtors: any[]) {
        baseCtors.forEach((baseCtor) => {
            Object.getOwnPropertyNames(baseCtor.prototype).forEach((name) => {
                Object.defineProperty(
                    derivedCtor.prototype,
                    name,
                    Object.getOwnPropertyDescriptor(baseCtor.prototype, name)
                );
            });
        });
    }

    public splitLines(lines: string): string {
        if (!lines) {
            return "";
        }
        return lines
            .split(";")
            .map((r) => "<div>" + r + "</div>")
            .join("");
    }

    public safeDivide(numerator, divisor): number {
        if (divisor === 0) {
            return 0;
        }

        return (
            Math.round(((numerator * 1.0) / (divisor * 1.0)) * 10000) / 10000
        );
    }

    public yearStartToDate(year): Date {
        return moment().set("year", year).startOf("year").toDate();
    }

    public yearStartToDateString(year): string {
        return moment()
            .set("year", year)
            .startOf("year")
            .format(this.moment_dateFormat);
    }

    public yearStopToDate(year): Date {
        return moment().set("year", year).startOf("year").toDate();
    }

    public yearStopToDateString(year): string {
        return moment()
            .set("year", year)
            .endOf("year")
            .format(this.moment_dateFormat);
    }

    public spinnerType(): string {
        const index = Math.floor(Math.random() * this.spinnerTypes.length);
        return this.spinnerTypes[index];
    }

    public stringCompare(s1: string, s2: string): boolean {
        const s1_empty = s1 == null || s1?.trim() === "";
        const s2_empty = s2 == null || s2?.trim() === "";

        return (s1_empty && s2_empty) || s1?.trim() === s2?.trim();
    }

    public isMotorist(ticket: tTicket_Session): boolean {
        return ticket.ticket_type === "T";
    }

    public identity(ticket: tTicket_Session): string {
        if (ticket.ticket_type === "T" && ticket.license_number != null) {
            return `${ticket.license_number} ${ticket.license_state}`;
        }
        return `${ticket.plate_number} ${ticket.plate_state}`;
    }

    toast_broadcast_message(req: { message: string; displayTime?: number }) {
        notify(
            {
                message: req.message,
                displayTime: req.displayTime ?? 1000 * 5,
                maxWidth: 300,
                closeOnClick: true,
                hideOnOutsideClick: true,
                //type: 'cc3-info',
                type: "info",
            },
            { position: "top right" }
        );
    }

    toast_success(req: { message: string; displayTime?: number }) {
        notify(
            {
                message: req.message,
                displayTime: req.displayTime ?? 1000 * 5,
                maxWidth: 300,
                closeOnClick: true,
                hideOnOutsideClick: true,
                type: "success",
            },
            { position: "top right" }
        );
    }

    toast_info(req: { message: string; displayTime?: number }) {
        notify(
            {
                message: req.message,
                displayTime: req.displayTime ?? 1000 * 5,
                maxWidth: 300,
                closeOnClick: true,
                hideOnOutsideClick: true,
                //type: 'cc3-info',
                type: "info",
            },
            { position: "top right" }
        );
    }

    toast_warn(req: { message: string; displayTime?: number }) {
        notify(
            {
                message: req.message,
                displayTime: req.displayTime ?? 1000 * 5,
                maxWidth: 300,
                closeOnClick: true,
                hideOnOutsideClick: true,
                type: "warning",
            },
            { position: "top right" }
        );
    }

    toast_error(req: { message: string; displayTime?: number }) {
        notify(
            {
                message: req.message,
                displayTime: req.displayTime ?? 1000 * 10,
                maxWidth: 300,
                closeOnClick: true,
                hideOnOutsideClick: true,
                type: "error",
            },
            { position: "top right" }
        );
    }

    toast_center(req: { message: string; displayTime?: number }) {
        notify(
            {
                message: req.message,
                displayTime: req.displayTime ?? 1000 * 3,
                maxWidth: 300,
                closeOnClick: true,
                hideOnOutsideClick: true,
                type: "cc3-info",
            },
            { position: "center" }
        );
    }
}
